import { Text } from '@app/components/core/typography/Text'
import Column from '@app/components/layout/Column'
import { useTranslations } from '@i18n/useLanguage'
import { faPlus } from '@fortawesome/pro-regular-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { useCallback } from 'react'
import { ContentBlockEditor, type TextEditBlock } from './ContentBlockEditor'
import { Card } from '../card/Card'
import { PageContentEditorProps } from './types/pageContentEditorProps'
import { emptyTextBlock } from '@app/pages/dashboard/initiative/shared/initiativeOverviewTextBlocks'

export { PageContentEditorBlock } from './types/pageContentEditorBlock'
export { PageContentEditorProps } from './types/pageContentEditorProps'

export const PageContentEditor = ({
  value,
  onValueChange,
  minBlocks = 0,
  maxBlocks = 10,
  style,
}: PageContentEditorProps) => {
  const t = useTranslations().app.dashboard.pageContentEditor

  const updateRow = useCallback(
    (index: number, next: TextEditBlock) => {
      const row = value[index]
      if (
        row &&
        row.title === next.title &&
        row.body === next.body &&
        row.type === next.type
      ) {
        return
      }
      onValueChange(
        value.map((row, i) => {
          if (i !== index) {
            return row
          }
          return {
            ...row,
            title: next.title,
            body: next.body,
            type: next.type,
          }
        }),
      )
    },
    [value, onValueChange],
  )

  const removeRow = useCallback(
    (index: number) => {
      if (value.length <= minBlocks) {
        return
      }
      onValueChange(value.filter((_, i) => i !== index))
    },
    [value, minBlocks, onValueChange],
  )

  const moveRow = useCallback(
    (index: number, delta: -1 | 1) => {
      const j = index + delta
      if (j < 0 || j >= value.length) {
        return
      }
      const next = [...value]
      const tmp = next[index]
      const swap = next[j]
      if (!tmp || !swap) {
        return
      }
      next[index] = swap
      next[j] = tmp
      onValueChange(next)
    },
    [value, onValueChange],
  )

  const addBlock = useCallback(() => {
    if (value.length >= maxBlocks) {
      return
    }
    onValueChange([...value, { ...emptyTextBlock() }])
  }, [value, maxBlocks, onValueChange])

  const canRemoveBlock = value.length > minBlocks
  const canAddBlock = value.length < maxBlocks

  return (
    <Column gap="1rem" alignItems="stretch" style={style}>
      {value.map((row, index) => (
        <ContentBlockEditor
          key={row.clientKey}
          block={row}
          onChange={(next) => updateRow(index, next)}
          onRemove={() => removeRow(index)}
          onMoveUp={() => moveRow(index, -1)}
          onMoveDown={() => moveRow(index, 1)}
          canMoveUp={index > 0}
          canMoveDown={index < value.length - 1}
          canRemove={canRemoveBlock}
          titlePlaceholder={row.titlePlaceholder}
          bodyPlaceholder={row.bodyPlaceholder}
        />
      ))}
      {canAddBlock ? (
        <Card
          onClick={addBlock}
          style={{
            width: '100%',
            minHeight: '5.25rem',
            '&:hover': {
              background: 'var(--gray-100)',
            },
            '&:focus-visible': {
              outline: '2px solid var(--brand-secondary)',
              outlineOffset: '2px',
            },
          }}
        >
          <Column
            style={{
              gap: '0.375rem',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <FontAwesomeIcon
              icon={faPlus}
              style={{
                width: '2.5rem',
                height: '2.5rem',
                color: 'var(--brand-secondary)',
              }}
            />
            <Text.Medium
              style={{
                color: 'var(--palette-blue-500)',
                textAlign: 'center',
              }}
            >
              {t.addBlock}
            </Text.Medium>
          </Column>
        </Card>
      ) : null}
    </Column>
  )
}
